"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BillingService } from "@/services/BillingService";
import type { BillingDashboardOutput } from "@/interfaces/billing";
import { numberToMoney } from "@/util/moneyConversion";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  CreditCard,
  Wallet,
  AlertTriangle,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface FinancialOverviewCardsProps {
  monthYear: string;
  previousMonthYear?: string;
  isLoading?: boolean;
}

export const FinancialOverviewCards: React.FC<FinancialOverviewCardsProps> = ({
  monthYear,
  previousMonthYear,
  isLoading = false,
}) => {
  const [data, setData] = useState<BillingDashboardOutput | null>(null);
  const [prevData, setPrevData] = useState<BillingDashboardOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const service = new BillingService();
        const response = await service.getDashboard({ monthYear });
        setData(response);

        if (previousMonthYear) {
          const prevResponse = await service.getDashboard({
            monthYear: previousMonthYear,
          });
          setPrevData(prevResponse);
        } else {
          setPrevData(null);
        }
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
        setError("Falha ao carregar dados financeiros");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [monthYear, previousMonthYear]);

  // Calcular a variação percentual entre dois valores
  const calculatePercentChange = (
    current: number,
    previous: number
  ): number => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  // Determinar se a variação é positiva ou negativa (para alguns indicadores, negativo pode ser bom)
  const isPositiveChange = (
    indicator: string,
    percentChange: number
  ): boolean => {
    // Para despesas, uma redução (percentual negativo) é positiva
    if (indicator === "expenses") return percentChange <= 0;
    // Para pagamentos pendentes, uma redução é positiva
    if (indicator === "pending") return percentChange <= 0;
    // Para outros indicadores (faturamento, recebimentos), um aumento é positivo
    return percentChange >= 0;
  };

  if (isLoading || loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={`skeleton-${i}`} className="overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                <Skeleton className="h-4 w-24" />
              </CardTitle>
              <Skeleton className="h-8 w-8 rounded-full" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-full mt-2" />
              <Skeleton className="h-4 w-20 mt-2" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="col-span-4">
          <CardContent className="pt-6 text-red-500 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            {error}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const cards = [
    {
      title: "Total Faturado",
      value: data.totalAmount,
      prevValue: prevData?.totalAmount,
      icon: <DollarSign className="h-5 w-5" />,
      color: "bg-blue-500",
      textColor: "text-blue-600",
      indicator: "revenue",
    },
    {
      title: "Total Recebido",
      value: data.paymentReceived,
      prevValue: prevData?.paymentReceived,
      icon: <Wallet className="h-5 w-5" />,
      color: "bg-green-500",
      textColor: "text-green-600",
      indicator: "received",
    },
    {
      title: "Total Despesas",
      value: data.totalExpanses,
      prevValue: prevData?.totalExpanses,
      icon: <CreditCard className="h-5 w-5" />,
      color: "bg-red-500",
      textColor: "text-red-600",
      indicator: "expenses",
    },
    {
      title: "Pagamentos Pendentes",
      value: data.pendingPayments,
      prevValue: prevData?.pendingPayments,
      icon: <AlertTriangle className="h-5 w-5" />,
      color: "bg-orange-500",
      textColor: "text-orange-500",
      indicator: "pending",
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {cards.map((card, index) => {
        const percentChange =
          card.prevValue !== undefined
            ? calculatePercentChange(card.value, card.prevValue)
            : 0;

        const positive = isPositiveChange(card.indicator, percentChange);

        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            whileHover={{ y: -5, transition: { duration: 0.2 } }}
            className="h-full"
          >
            <Card
              className="h-full border-l-4"
              style={{
                borderLeftColor: card.color.replace("bg-", "rgb(var(--"),
              }}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  {card.title}
                </CardTitle>
                <div className={`${card.color} p-2 rounded-full text-white`}>
                  {card.icon}
                </div>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${card.textColor}`}>
                  {numberToMoney(card.value)}
                </div>

                {card.prevValue !== undefined && (
                  <div className="flex items-center mt-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant={positive ? "success" : "destructive"}
                            className="flex items-center"
                          >
                            {positive ? (
                              <TrendingUp className="h-3 w-3 mr-1" />
                            ) : (
                              <TrendingDown className="h-3 w-3 mr-1" />
                            )}
                            {Math.abs(percentChange).toFixed(1)}%
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {positive ? "Aumento" : "Redução"} de{" "}
                            {Math.abs(percentChange).toFixed(1)}% em relação ao
                            período anterior
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Período anterior:{" "}
                            {numberToMoney(card.prevValue || 0)}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
};
